import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowRight, Star, Instagram, Sparkles, ChevronRight } from "lucide-react";
import ProductCard from "@/components/ProductCard";

import { API_BASE as API } from "@/config/api";
import { getWhatsAppUrl } from "@/config/contact";

const OCCASIONS = [
  { name: "Birthday", icon: "🎂", color: "bg-pink-50 text-pink-700 border-pink-200" },
  { name: "Anniversary", icon: "💕", color: "bg-red-50 text-red-700 border-red-200" },
  { name: "Wedding", icon: "💍", color: "bg-amber-50 text-amber-700 border-amber-200" },
  { name: "Valentine's Day", icon: "🌹", color: "bg-rose-50 text-rose-700 border-rose-200" },
  { name: "Baby Shower", icon: "🍼", color: "bg-sky-50 text-sky-700 border-sky-200" },
  { name: "Engagement", icon: "💎", color: "bg-purple-50 text-purple-700 border-purple-200" },
];

const TESTIMONIALS = [
  { name: "Priya Sharma", city: "Mumbai", rating: 5, text: "Absolutely loved the chocolate bouquet! The packaging was exquisite and my sister was over the moon. Will definitely order again!", img: "https://images.unsplash.com/photo-1494790108755-2616b612b5bc?w=100&q=80" },
  { name: "Rahul Mehta", city: "Delhi", rating: 5, text: "Ordered the anniversary love box and it exceeded all expectations. The attention to detail is incredible. Petal Pookie is our go-to for all occasions!", img: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&q=80" },
  { name: "Neha Gupta", city: "Pune", rating: 5, text: "The haldi favor cones were perfect for our wedding! So many guests asked where we got them from. Highly recommend for wedding favors.", img: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&q=80" },
];

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };
const stagger = { visible: { transition: { staggerChildren: 0.1 } } };

const instaImageContext = require.context("@/images/Insta_images", false, /\.jpe?g$/i);
const INSTAGRAM_IMAGES = instaImageContext.keys().sort().map(instaImageContext);

export default function HomePage() {
  const [featured, setFeatured] = useState([]);
  const [newArrivals, setNewArrivals] = useState([]);
  const [categories, setCategories] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/products?featured=true&limit=4`).then(r => setFeatured(r.data.products || [])).catch(() => {});
    axios.get(`${API}/products?is_new=true&limit=4`).then(r => setNewArrivals(r.data.products || [])).catch(() => {});
    axios.get(`${API}/categories`).then(r => setCategories(r.data || [])).catch(() => {});
  }, []);

  const marqueeItems = categories.length
    ? categories.map(c => c.name)
    : ["Chocolate Bouquets", "Rose Bouquets", "Gift Hampers", "Wedding Gifts", "Birthday Gifts"];

  return (
    <div className="bg-petal-beige" data-testid="home-page">

      {/* ── Hero Section ─────────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-[#F5F0E8] min-h-[92vh] flex items-center" data-testid="hero-section">
        {/* Decorative elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full bg-petal-gold/5" />
          <div className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full bg-petal-olive/5" />
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-8 py-24 grid md:grid-cols-2 gap-16 items-center w-full">
          {/* Text */}
          <motion.div initial="hidden" animate="visible" variants={stagger} className="z-10">
            <motion.p variants={fadeUp} className="font-dancing text-petal-gold text-2xl mb-4">
              Made with love, wrapped with happiness
            </motion.p>
            <motion.h1 variants={fadeUp} className="font-playfair text-5xl sm:text-6xl lg:text-7xl text-petal-olive-text leading-tight mb-6">
              Every Bouquet<br />
              <span className="text-petal-olive italic">Tells a Story</span>
            </motion.h1>
            <motion.p variants={fadeUp} className="font-outfit text-base text-petal-muted max-w-md mb-10 leading-relaxed">
              Handcrafted customized gifts for every special occasion. From chocolate bouquets to luxury hampers — let us create something beautiful for you.
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-wrap gap-4">
              <Link to="/shop" data-testid="hero-shop-now" className="btn-primary inline-flex items-center gap-2">
                Shop Now <ArrowRight className="w-4 h-4" />
              </Link>
              <a href={getWhatsAppUrl("Hi! I'd like to customize a gift.")}
                target="_blank" rel="noopener noreferrer" data-testid="hero-whatsapp" className="btn-gold inline-flex items-center gap-2">
                Customize Gift
              </a>
            </motion.div>

            <motion.div variants={fadeUp} className="mt-12 flex items-center gap-8">
              {[["500+", "Happy Customers"], ["100%", "Handmade"]].map(([v, l]) => (
                <div key={l} className="text-center">
                  <p className="font-playfair text-2xl text-petal-olive font-semibold">{v}</p>
                  <p className="font-outfit text-xs text-petal-muted uppercase tracking-widest">{l}</p>
                </div>
              ))}
            </motion.div>
          </motion.div>

          {/* Image grid */}
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }}
            className="relative hidden md:grid grid-cols-2 gap-4">
            {[
              <img src={require("@/images/Insta_images/WhatsApp Image 2026-07-14 at 3.53.03 PM.jpeg")} alt="Gift" className="w-full aspect-square object-cover" />,
              <img src={require("@/images/Insta_images/WhatsApp Image 2026-07-14 at 4.09.28 PM.jpeg")} alt="Gift" className="w-full aspect-square object-cover" />,
              <img src={require("@/images/Insta_images/WhatsApp Image 2026-07-14 at 4.09.52 PM.jpeg")} alt="Gift" className="w-full aspect-square object-cover" />,
              <img src={require("@/images/Insta_images/WhatsApp Image 2026-07-14 at 4.09.31 PM.jpeg")} alt="Gift" className="w-full aspect-square object-cover" />,
            ]}
          </motion.div>
        </div>

        {/* Scrolling ticker */}
        <div className="absolute bottom-0 left-0 right-0 bg-petal-olive py-3 overflow-hidden">
          <div className="flex gap-12 animate-marquee whitespace-nowrap" style={{animation: "marquee 20s linear infinite"}}>
            {[...marqueeItems, ...marqueeItems].map((t, i) => (
              <span key={`${t}-${i}`} className="font-outfit text-sm text-petal-beige uppercase tracking-widest">✦ {t}</span>
            ))}
          </div>
        </div>
        <style>{`@keyframes marquee { from { transform: translateX(0) } to { transform: translateX(-50%) } }`}</style>
      </section>

      {/* ── Shop by Category ─────────────────────────────────────────── */}
      {categories.length > 0 && (
        <section className="py-20 bg-[#F5F0E8]" data-testid="shop-categories">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-12">
              <motion.p variants={fadeUp} className="section-subtitle mb-2">Browse Collections</motion.p>
              <motion.h2 variants={fadeUp} className="section-title">Shop by Category</motion.h2>
            </motion.div>
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
              className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {categories.map(cat => (
                <motion.div key={cat.id} variants={fadeUp}>
                  <Link to={`/shop?category=${encodeURIComponent(cat.name)}`} data-testid={`home-category-${cat.id}`}
                    className="group block bg-white border border-petal-border overflow-hidden hover:-translate-y-1 transition-all duration-200 hover:shadow-md">
                    <div className="aspect-square bg-petal-beige-alt overflow-hidden">
                      {cat.image ? (
                        <img src={cat.image} alt={cat.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center font-playfair text-3xl text-petal-olive/30">
                          {cat.name.charAt(0)}
                        </div>
                      )}
                    </div>
                    <div className="p-3 text-center">
                      <p className="font-outfit text-sm font-medium text-petal-olive-text group-hover:text-petal-olive transition-colors line-clamp-2">{cat.name}</p>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>
      )}

      {/* ── Occasion Categories ──────────────────────────────────────── */}
      <section className="py-20 bg-petal-beige" data-testid="occasion-categories">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-12">
            <motion.p variants={fadeUp} className="section-subtitle mb-2">Shop by Occasion</motion.p>
            <motion.h2 variants={fadeUp} className="section-title">Find the Perfect Gift</motion.h2>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 max-w-4xl mx-auto">
            {OCCASIONS.map(occ => (
              <motion.div key={occ.name} variants={fadeUp} className="h-full">
                <Link to={`/shop?occasion=${encodeURIComponent(occ.name)}`} data-testid={`occasion-${occ.name.toLowerCase()}`}
                  className={`flex flex-col items-center justify-center gap-2 p-4 border rounded-sm h-full min-h-[104px] w-full hover:-translate-y-1 transition-all duration-200 ${occ.color}`}>
                  <span className="text-2xl shrink-0">{occ.icon}</span>
                  <span className="font-outfit text-xs text-center font-medium leading-tight min-h-[2.5rem] flex items-center justify-center">{occ.name}</span>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Featured Products ─────────────────────────────────────────── */}
      <section className="py-20 bg-[#F5F0E8]" data-testid="featured-products">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
            className="flex flex-col sm:flex-row sm:items-end justify-between mb-12 gap-4">
            <div>
              <motion.p variants={fadeUp} className="section-subtitle mb-2">Handpicked for You</motion.p>
              <motion.h2 variants={fadeUp} className="section-title">Our Bestsellers</motion.h2>
            </div>
            <motion.div variants={fadeUp}>
              <Link to="/shop?bestseller=true" className="btn-outline inline-flex items-center gap-2 text-xs">
                View All <ChevronRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
            className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {featured.length > 0 ? featured.map(p => (
              <motion.div key={p.id} variants={fadeUp}>
                <ProductCard product={p} />
              </motion.div>
            )) : [...Array(4)].map((_, i) => (
              <div key={i} className="bg-white border border-petal-border h-80 animate-pulse" />
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Brand Story Banner ───────────────────────────────────────── */}
      <section className="py-24 bg-petal-olive-text text-petal-beige overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 md:px-8 grid md:grid-cols-2 gap-16 items-center">
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <p className="font-dancing text-petal-gold text-xl mb-4">Our Promise</p>
            <h2 className="font-playfair text-4xl sm:text-5xl text-petal-beige mb-6 leading-tight">
              Every Bouquet<br />Tells a Story
            </h2>
            <p className="font-outfit text-petal-border/80 text-base leading-relaxed mb-8">
              At Petal Pookie, we believe gifts should be as unique as the people who receive them. Each creation is handcrafted with love, using only premium ingredients and materials. We put our heart into every bouquet, hamper, and gift box.
            </p>
            <div className="grid grid-cols-2 gap-6 mb-8">
              {[["Handmade", "Every piece crafted by hand"], ["Premium Quality", "Only the finest materials"], ["Custom Gifting", "Personalized to your vision"], ["Local Delivery", "Fresh & on time"]].map(([t, d]) => (
                <div key={t} className="border-l-2 border-petal-gold pl-4">
                  <p className="font-outfit text-sm font-semibold text-petal-gold mb-1">{t}</p>
                  <p className="font-outfit text-xs text-petal-border/70">{d}</p>
                </div>
              ))}
            </div>
            <Link to="/about" className="inline-flex items-center gap-2 font-outfit text-sm text-petal-gold uppercase tracking-widest hover:gap-3 transition-all">
              Our Story <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
            className="relative">
            <img src="https://images.unsplash.com/photo-1759563874671-22b56a3bc6d3?w=600&q=80" alt="Our Story"
              className="w-full aspect-square object-cover" />
            <div className="absolute -bottom-4 -right-4 bg-petal-gold p-6 w-32 h-32 flex flex-col items-center justify-center">
              <p className="font-playfair text-3xl text-white font-semibold">1+</p>
              <p className="font-outfit text-xs text-white/80 uppercase tracking-wider text-center">Years of Joy</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── New Arrivals ──────────────────────────────────────────────── */}
      {newArrivals.length > 0 && (
        <section className="py-20 bg-petal-beige" data-testid="new-arrivals">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
              className="flex flex-col sm:flex-row sm:items-end justify-between mb-12 gap-4">
              <div>
                <motion.p variants={fadeUp} className="section-subtitle mb-2">Just In</motion.p>
                <motion.h2 variants={fadeUp} className="section-title">New Arrivals</motion.h2>
              </div>
              <motion.div variants={fadeUp}>
                <Link to="/shop?is_new=true" className="btn-outline inline-flex items-center gap-2 text-xs">
                  View All <ChevronRight className="w-4 h-4" />
                </Link>
              </motion.div>
            </motion.div>
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
              className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {newArrivals.map(p => (
                <motion.div key={p.id} variants={fadeUp}><ProductCard product={p} /></motion.div>
              ))}
            </motion.div>
          </div>
        </section>
      )}

      {/* ── Testimonials ─────────────────────────────────────────────── */}
      <section className="py-20 bg-[#F5F0E8]" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-12">
            <motion.p variants={fadeUp} className="section-subtitle mb-2">Love from Customers</motion.p>
            <motion.h2 variants={fadeUp} className="section-title">What They Say</motion.h2>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
            className="grid md:grid-cols-3 gap-8">
            {TESTIMONIALS.map(t => (
              <motion.div key={t.name} variants={fadeUp}
                className="bg-white p-8 border border-petal-border shadow-[0_4px_20px_rgba(92,107,58,0.06)] hover:-translate-y-1 transition-transform duration-300">
                <div className="flex gap-1 mb-4">
                  {[...Array(t.rating)].map((_, i) => <Star key={i} className="w-4 h-4 fill-petal-gold text-petal-gold" />)}
                </div>
                <p className="font-outfit text-sm text-petal-muted leading-relaxed mb-6 italic">"{t.text}"</p>
                <div className="flex items-center gap-3">
                  <img src={t.img} alt={t.name} className="w-10 h-10 rounded-full object-cover border-2 border-petal-border" />
                  <div>
                    <p className="font-outfit text-sm font-semibold text-petal-olive-text">{t.name}</p>
                    <p className="font-outfit text-xs text-petal-muted">{t.city}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Instagram Section ─────────────────────────────────────────── */}
      <section className="py-20 bg-petal-beige" data-testid="instagram-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.p variants={fadeUp} className="section-subtitle mb-2">Follow for More</motion.p>
            <motion.h2 variants={fadeUp} className="section-title mb-2">Petal Pookie</motion.h2>
            <motion.p variants={fadeUp} className="font-outfit text-sm text-petal-muted mb-8">Follow us for beautiful creations & behind the scenes</motion.p>
            <motion.div variants={fadeUp}>
              <a href="https://www.instagram.com/petal_pookie.08" target="_blank" rel="noopener noreferrer"
                data-testid="follow-instagram-btn" className="btn-primary inline-flex items-center gap-2">
                <Instagram className="w-4 h-4" /> Follow on Instagram
              </a>
            </motion.div>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
            className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 mt-10">
            {INSTAGRAM_IMAGES.map((url, i) => (
              <motion.a key={i} variants={fadeUp} href="https://www.instagram.com/petal_pookie.08" target="_blank" rel="noopener noreferrer"
                className="relative group aspect-square overflow-hidden">
                <img src={url} alt={`Petal Pookie creation ${i + 1}`} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                <div className="absolute inset-0 bg-petal-olive/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <Instagram className="w-6 h-6 text-white" />
                </div>
              </motion.a>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── WhatsApp CTA ─────────────────────────────────────────────── */}
      <section className="py-16 bg-[#25D366]">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.h2 variants={fadeUp} className="font-playfair text-3xl sm:text-4xl text-white mb-4">
              Let's Create Your Perfect Gift
            </motion.h2>
            <motion.p variants={fadeUp} className="font-outfit text-white/90 mb-8">
              Have a custom order in mind? Chat with us on WhatsApp and we'll bring your vision to life.
            </motion.p>
            <motion.div variants={fadeUp}>
              <a href={getWhatsAppUrl("Hi! I'd like to place a custom order.")}
                target="_blank" rel="noopener noreferrer" data-testid="whatsapp-cta"
                className="inline-flex items-center gap-3 bg-white text-[#25D366] font-outfit font-semibold px-8 py-4 hover:shadow-xl transition-all">
                <Sparkles className="w-5 h-5" />
                WhatsApp Us Now
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
